import { useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { isNumeric } from "../../utils";
import { getCountOrderByArea, getCountOrderByTherapist, getOrderForOutsourcing } from "../../api/revenue";
import dayjs from "dayjs";

const useOutsourcingStatementMaster = () => {
  const [totalData, setTotalData] = useState();
  const [arrOrders, setArrOrders] = useState([]);
  const [activeSortColumn, setActiveSortColumn] = useState(0);
  const [sortColumnDirection, setSortColumnDirection] = useState("desc");
  const [sortedData, setSortedData] = useState([]);

  const [areaOptions, setAreaOptions] = useState([]);
  const [therapistOptions, setTherapistOptions] = useState([]);
  const [total, setTotal] = useState(0);
  const [params, setParms] = useState({
    page: 1,
    per_page: 100,
    date: (() => {
      const now = dayjs();
      const currentHour = now.hour();
      // If current time is between 0:00-5:00, return previous day
      if (currentHour >= 0 && currentHour < 5) {
        return now.subtract(1, "day").format("YYYY-MM-DD");
      }
      return now.format("YYYY-MM-DD");
    })(),
    area_names: [],
    therapist_ids: [],
    start_date: "",
    end_date: "",
    is_group_by_therapist_and_date: 1,
  });

  const { data } = useQuery(
    ["OrdersForOutsourcing", params],
    () => {
      return getOrderForOutsourcing(params);
    },
    {
      keepPreviousData: true,
    }
  );

  const { data: dataArea } = useQuery(
    [
      "countOrderByArea",
      params.date,
      params.start_date,
      params.end_date,
      params.therapist_ids,
      params.page,
      params.per_page,
      params.is_group_by_therapist_and_date,
    ],
    () => {
      const queryParams = {
        page: params.page,
        per_page: params.per_page,
        date: params.date,
        start_date: params.start_date,
        end_date: params.end_date,
        therapist_ids: params.therapist_ids,
        is_group_by_therapist_and_date: params.is_group_by_therapist_and_date,
      };

      return getCountOrderByArea(queryParams);
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: dataTherapist } = useQuery(
    ["countOrderByTherapist", params.date, params.start_date, params.end_date,
      params.area_names,
      params.page,
      params.per_page,
      params.is_group_by_therapist_and_date,
    ],
    () => {
      const queryParams = {
        page: params.page,
        per_page: params.per_page,
        date: params.date,
        start_date: params.start_date,
        end_date: params.end_date,
        area_names: params.area_names,
        is_group_by_therapist_and_date: params.is_group_by_therapist_and_date,
      };
      return getCountOrderByTherapist(queryParams);
    },
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (data) {
      setArrOrders(
        transformOrdersData(data.data.data)
      );
      setTotal(data.data.total);
      setTotalData(data.data.item_total);
    }
  }, [data]);

  useEffect(() => {
    setSortedData(arrOrders.sort((a, b) => b.id - a.id));
  }, [arrOrders]);

  useEffect(() => {
    if (dataArea) {
      setAreaOptions(
        dataArea.data.map((item) => ({
          label: item.area,
          value: item.area,
          count: item.count_order,
        }))
      );
    }
  }, [dataArea]);

  useEffect(() => {
    if (dataTherapist) {
      setTherapistOptions(
        dataTherapist.data.map((item) => ({
          label: item.name,
          value: item.id,
          count: item.count_order,
        }))
      );
    }
  }, [dataTherapist]);

  const transformOrdersData = (data) => {
    return data.map((order) => ({
      id: order.id.toString(),
      area: order.area,
      therapist_name: order.therapist_name,
      therapist_amount: order.therapist_amount,
      sales: order.sales,
      equipment_cost: order.equipment_cost,
      therapist_amount_after_equipment: order.therapist_amount_after_equipment,
      sales_after_equipment: order.sales_after_equipment,
    }));
  };

  const handleSort = useCallback(
    (columnIndex) => {
      const isSorted = activeSortColumn === columnIndex;
      let direction = isSorted
        ? sortColumnDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";
      sortedData.sort((a, b) => {
        let aValue = a[Object.keys(a)[columnIndex]];
        let bValue = b[Object.keys(b)[columnIndex]];

        // if aValue and bValue are numberic, sort parseInt
        if (isNumeric(aValue) && isNumeric(bValue)) {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        }

        if (aValue < bValue) return direction === "asc" ? -1 : 1;
        if (aValue > bValue) return direction === "asc" ? 1 : -1;
        return 0;
      });
      setSortedData([...sortedData]);
      setActiveSortColumn(columnIndex);
      setSortColumnDirection(direction);
    },
    [activeSortColumn, sortColumnDirection, sortedData]
  );

  const handleUpdateToggleIsGroupByTherapistAndDate = () => {
    setParms((prev) => ({
      ...prev,
      is_group_by_therapist_and_date: prev.is_group_by_therapist_and_date === 1 ? 0 : 1,
    }));
  };

  return {
    activeSortColumn,
    sortColumnDirection,
    sortedData,
    total,
    handleSort,
    params,
    setParms,
    areaOptions,
    therapistOptions,
    totalData,
    handleUpdateToggleIsGroupByTherapistAndDate,
  };
};

export default useOutsourcingStatementMaster;
